import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useParams } from "react-router";

import { CourseProvider } from "../../context/course-context";
import { HasbotProvider } from "../../context/hasBot-context";
import { LocalizationProvider, SupportedLocales } from "../../context/localization-context";
import { useAppState } from "../../hooks/useAppState";
import { usePersistedSettings } from "../../hooks/usePersistedSettings";
import * as locales from "../../locale/index";
import customTheme from "../../styles/theme";
import { TelemetryLayer } from "../../telemetry/TelemetryLayer";
import { ProgressModal } from "../Progress/ProgressModal";

const PlayerWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { courseId: courseIdParam } = useParams<{ courseId?: string }>();
  const { setCourse } = useAppState();
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [locale, setLocale] = useState<SupportedLocales>("enUS");
  const { mode, setMode } = usePersistedSettings();

  // Set courseId from URL as soon as we mount so CourseProvider can start InitCoursePageQuery
  // before the lazy Player chunk loads. Saves 1–2s on direct navigation (e.g. /12345).
  useLayoutEffect(() => {
    const numeric = Number(courseIdParam);
    if (courseIdParam !== undefined && !Number.isNaN(numeric) && numeric > 0) {
      setCourse(numeric);
    }
  }, [courseIdParam, setCourse]);

  const preferredMode = prefersDarkMode ? "dark" : "light";

  const theme = useMemo(
    () => createTheme(customTheme(mode || preferredMode), locales[locale]),
    [locale, mode, preferredMode],
  );

  useEffect(() => {
    if (!mode) {
      setMode(preferredMode);
    }
  }, [mode, preferredMode, setMode]);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider value={{ locale, setLocale }}>
          <TelemetryLayer>
            <ProgressModal />
            <CourseProvider>
              <HasbotProvider>{children}</HasbotProvider>
            </CourseProvider>
          </TelemetryLayer>
        </LocalizationProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default PlayerWrapper;
